import { useCallback, useState } from 'react';
import { LinkRenderer } from '@/components/elements/Link';
import { IComponentGlobalFooter } from '@/graphql/__generated__/global/types';
import { determineLocaleCountry } from '@/helpers/determineLocale';
import classNames from '@/lib/classNames';
import Image from 'next/image';
import { useRouter } from 'next/router';
import Button from '../elements/Button';
import { useToast } from '../elements/Toast/useToast';
import { Icon } from './Icons';
import { MailingListSignupModal } from './MailingListSignupModal';

export type FooterProps = {
    partners: React.ReactNode;
    footer: IComponentGlobalFooter;
};

export const Footer = ({ partners, footer }: FooterProps) => {
    const [email, setEmail] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const router = useRouter();
    const localeCountry = determineLocaleCountry(router.locale);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setEmail(event.target.value);
    };

    const { toast } = useToast();

    const handleSubmit = useCallback((event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setIsModalOpen(true);
    }, []);

    const handleNameSubmit = useCallback(
        async ({ firstName, lastName }: { firstName: string; lastName: string }) => {
            const response = await fetch('/api/signup', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email,
                    firstName,
                    lastName,
                    localeCountry,
                }),
            });

            setIsModalOpen(false);
            toast({
                description: response.ok ? '✅ Your email has been added to the Star RV Mailing List' : '❌ There was an error adding your email to the Star RV Mailing List',
            });
        },
        [toast, email, localeCountry]
    );

    if (!footer) {
        return null;
    }

    const { footerColumns, underFooterLinks } = footer;

    return (
        <footer aria-label="Site Footer" className="flex w-full bg-primary text-shell-25 mt-8 lg:mt-20 rounded-tl-[30px] rounded-tr-[30px]">
            <div className="max-w-footer box-content w-full mx-auto px-4 md:px-[4.75rem] pt-20 pb-8">
                <div className="flex flex-col lg:flex-row gap-9 items-center md:items-start md:justify-between font-light text-base leading-6">
                    <div className="max-w-[35.938rem] w-full flex flex-col gap-6">
                        <Image src="/logo.svg" alt="Star RV Logo" width={250} height={250} />
                        <p className="text-base">Join our Star RV community and stay up to date with offers, inspiration and more.</p>
                        <form onSubmit={handleSubmit}>
                            <div className="relative max-w-lg">
                                <label className="sr-only" htmlFor="email">
                                    Email
                                </label>
                                <div className="flex gap-2.5">
                                    <input
                                        className="leading-normal w-full rounded-full border-gray-200 bg-white py-4 px-8 text-sm font-medium text-primary"
                                        id="email"
                                        type="email"
                                        placeholder="Enter your email"
                                        value={email}
                                        onChange={handleChange}
                                    />
                                    <Button className="px-8" size="sm" colour="white" variant="outline" type="submit">
                                        Submit
                                    </Button>
                                </div>
                            </div>
                        </form>
                    </div>

                    <div className={`lg:max-w-[51.25rem] w-full flex flex-col md:flex-row lg:justify-between gap-8 md:gap-5 md:pt-8`}>
                        {footerColumns.map((column) => (
                            <div className="w-full md:max-w-[16.25rem] text-center md:text-left" key={column.id}>
                                <p className="font-semibold text-base uppercase whitespace-nowrap">{column.footerTitle}</p>
                                <nav aria-label="Footer About Nav" className="mt-4">
                                    <ul className="space-y-4" key={column.id}>
                                        {column.links.map((item) => (
                                            <li key={item.id}>
                                                <LinkRenderer className="flex justify-center md:justify-start items-center" {...item}>
                                                    {item.linkToExternalPage &&
                                                        (item.linkToExternalPage.includes('facebook') ||
                                                            item.linkToExternalPage.includes('youtube') ||
                                                            item.linkToExternalPage.includes('instagram')) && (
                                                            <>
                                                                {item.linkToExternalPage.includes('facebook') && <Icon id="facebook" className="w-4 h-4 fill-white block mr-2" />}
                                                                {item.linkToExternalPage.includes('youtube') && <Icon id="youtube" className="w-4 h-4 fill-white block mr-2" />}
                                                                {item.linkToExternalPage.includes('instagram') && <Icon id="instagram" className="w-4 h-4 fill-white block mr-2" />}
                                                            </>
                                                        )}
                                                    {item.label}
                                                </LinkRenderer>
                                            </li>
                                        ))}
                                    </ul>
                                </nav>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="pt-9 flex items-center gap-9 justify-between flex-col-reverse md:flex-row text-center md:text-left">
                    <div className="flex w-100 gap-4 flex-col justify-center">
                        <ul className="flex w-100 gap-4 justify-center flex-row md:justify-end">
                            {underFooterLinks.map((link, index) => (
                                <li key={link.id}>
                                    <LinkRenderer className={`inline-block underline`} {...link} forceTarget="_blank">
                                        <p className={classNames('small', index !== underFooterLinks.length - 1 && 'pr-4 border-r')}>{link.label}</p>
                                    </LinkRenderer>
                                </li>
                            ))}
                        </ul>
                        <p className="text-sm">{new Date().getFullYear()} Star RV &copy;, All rights reserved.</p>
                    </div>
                    {partners}
                </div>
            </div>
            <MailingListSignupModal open={isModalOpen} onOpenChange={setIsModalOpen} onSubmit={handleNameSubmit} />
        </footer>
    );
};
